import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Radio } from 'lucide-react';

const VuBars = ({ playing }) => {
  const bars = Array.from({ length: 5 });
  return (
    <div className="flex items-end gap-1 h-6">
      {bars.map((_, i) => (
        <span
          key={i}
          className={`w-1 bg-amber rounded-sm ${playing ? 'vu-bar' : ''}`}
          style={{
            height: playing ? '100%' : '20%',
            animationDelay: `${i * 0.12}s`,
            animationDuration: `${0.5 + (i % 3) * 0.15}s`,
            opacity: playing ? 1 : 0.35,
          }}
        />
      ))}
    </div>
  );
};

const RadioPlayer = ({
  streamUrl,
  qualities,            // optional: [{ id, label, url, kbps }] — when provided, shows a switcher
  title,
  subtitle,
  logo,
  accentClass = 'from-amber to-amber-dark',
  ringClass = 'ring-amber/40',
  testIdPlay,
  testIdPause,
  testIdVolume,
  testIdQuality,        // base test id for quality switch buttons (e.g. 'majestic-quality')
  variant = 'primary',
}) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [loading, setLoading] = useState(false);
  const [qualityIdx, setQualityIdx] = useState(0);

  // Resolve the active stream URL & label
  const activeUrl = qualities && qualities.length ? qualities[qualityIdx].url : streamUrl;
  const activeKbps = qualities && qualities.length ? qualities[qualityIdx].kbps : 320;

  // Mirror the current stream URL into a ref so toggle()/switchQuality() always
  // read the latest value even across rapid state transitions.
  const activeUrlRef = useRef(activeUrl);
  useEffect(() => { activeUrlRef.current = activeUrl; }, [activeUrl]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const toggle = async () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      setLoading(true);
      try {
        const url = activeUrlRef.current;
        audioRef.current.src = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now();
        audioRef.current.load();
        await audioRef.current.play();
        setPlaying(true);
      } catch (e) {
        console.error('Audio play failed', e);
      } finally {
        setLoading(false);
      }
    }
  };

  const switchQuality = async (idx) => {
    if (idx === qualityIdx) return;
    setQualityIdx(idx);
    if (!audioRef.current) return;
    if (playing) {
      // Hot-swap the source while playing
      const next = qualities[idx];
      try {
        audioRef.current.pause();
        audioRef.current.src = next.url + (next.url.includes('?') ? '&' : '?') + 't=' + Date.now();
        audioRef.current.load();
        await audioRef.current.play();
      } catch (e) {
        console.error('Quality switch failed', e);
        setPlaying(false);
      }
    }
  };

  const isPrimary = variant === 'primary';

  return (
    <div
      className={`
        relative overflow-hidden
        ${isPrimary ? 'p-8 sm:p-10' : 'p-6 sm:p-8'}
        rounded-3xl border border-amber/20
        bg-gradient-to-br from-espresso via-vinyl to-black
        shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8)]
        w-full max-w-full
      `}
    >
      {/* Decorative inner glow */}
      <div className="absolute -inset-px rounded-3xl pointer-events-none" style={{
        background: 'radial-gradient(800px 200px at 20% 0%, rgba(245,158,11,0.10), transparent 60%)'
      }} />

      <div className="relative flex flex-col items-center xl:flex-row xl:items-stretch xl:text-left gap-6 xl:gap-8">
        {/* Spinning vinyl with logo */}
        <div className={`relative ${isPrimary ? 'w-32 h-32 sm:w-40 sm:h-40' : 'w-24 h-24 sm:w-28 sm:h-28'} shrink-0`}>
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-black via-zinc-900 to-black border border-white/5" />
          {/* Vinyl grooves */}
          <div className="absolute inset-2 rounded-full border border-white/[0.04]" />
          <div className="absolute inset-4 rounded-full border border-white/[0.04]" />
          <div className="absolute inset-6 rounded-full border border-white/[0.04]" />
          {/* Logo center */}
          <div className={`absolute inset-0 flex items-center justify-center ${playing ? 'animate-vinyl' : ''}`}>
            <img
              src={logo}
              alt={title}
              className={`${isPrimary ? 'w-24 h-24 sm:w-28 sm:h-28' : 'w-16 h-16 sm:w-20 sm:h-20'} rounded-full object-cover`}
              style={{ boxShadow: '0 0 30px rgba(245,158,11,0.25) inset' }}
            />
          </div>
          {/* Center pin */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-amber shadow-[0_0_12px_rgba(245,158,11,0.8)]" />
        </div>

        {/* Info & Controls */}
        <div className="flex-1 w-full min-w-0 text-center xl:text-left">
          <div className="flex items-center justify-center xl:justify-between gap-2 mb-1.5 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2 h-2 rounded-full bg-red-500 live-pulse shrink-0" />
              <span className="text-[10px] sm:text-xs tracking-[0.25em] uppercase text-amber font-bold truncate">
                {typeof activeKbps === 'number' ? `Live · ${activeKbps} kbps` : `Live · ${activeKbps}`}
              </span>
            </div>

            {/* Quality switcher (only when multiple qualities are provided) */}
            {qualities && qualities.length > 1 && (
              <div className="flex items-center gap-1 p-1 rounded-full border border-amber/25 bg-vinyl/60">
                {qualities.map((q, idx) => (
                  <button
                    key={q.id}
                    data-testid={testIdQuality ? `${testIdQuality}-${q.id}` : undefined}
                    onClick={() => switchQuality(idx)}
                    className={`
                      px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-body font-bold
                      transition-colors
                      ${idx === qualityIdx
                        ? 'bg-amber text-vinyl'
                        : 'text-cream/70 hover:text-amber'}
                    `}
                    aria-pressed={idx === qualityIdx}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <h3 className={`${isPrimary ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'} font-display text-cream leading-tight`}>
            {title}
          </h3>
          <p className="text-sm sm:text-base font-serif-italic text-amber-light/80 mt-1">{subtitle}</p>

          <div className="flex items-center justify-center xl:justify-start gap-4 sm:gap-5 mt-5">
            <button
              data-testid={playing ? testIdPause : testIdPlay}
              onClick={toggle}
              disabled={loading}
              aria-label={playing ? 'Pause' : 'Play'}
              className={`
                relative shrink-0 group
                w-14 h-14 sm:w-16 sm:h-16 rounded-full
                bg-gradient-to-br ${accentClass}
                flex items-center justify-center
                shadow-[0_0_25px_rgba(245,158,11,0.4)]
                ring-4 ${ringClass}
                transition-transform duration-200 hover:scale-105 active:scale-95
                disabled:opacity-60
              `}
            >
              {loading ? (
                <Radio className="w-6 h-6 text-vinyl animate-pulse" />
              ) : playing ? (
                <Pause className="w-6 h-6 sm:w-7 sm:h-7 text-vinyl" fill="currentColor" />
              ) : (
                <Play className="w-6 h-6 sm:w-7 sm:h-7 text-vinyl ml-0.5" fill="currentColor" />
              )}
            </button>

            <VuBars playing={playing} />

            <div className="flex-1 flex items-center gap-2 min-w-0">
              {volume === 0 ? (
                <VolumeX className="w-4 h-4 text-amber/70 shrink-0" />
              ) : (
                <Volume2 className="w-4 h-4 text-amber/70 shrink-0" />
              )}
              <input
                data-testid={testIdVolume}
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                aria-label="Volume"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <audio ref={audioRef} preload="none" />
    </div>
  );
};

export default RadioPlayer;
